import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "./components/ui/sonner";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CookiePolicy from "./pages/CookiePolicy";
import CookieBanner from "./components/CookieBanner";
import { PWAUpdateNotice } from "./components/PWAUpdateNotice";
import ImpersonationBanner from "./components/ImpersonationBanner";
import ImpersonateCallback from "./pages/ImpersonateCallback";
import LottoPubblico from "./pages/LottoPubblico";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { FeatureProvider } from "./context/FeatureContext";
import { ReactivateProvider } from "./context/ReactivateContext";
import { ConfirmProvider } from "./hooks/useConfirm";
import LocaleChooser from "./components/LocaleChooser";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import OperatoreDashboard from "./pages/OperatoreDashboard";
import AvvisiScorte from "./pages/operatore/AvvisiScorte";
import Lotti from "./pages/Lotti";
import ArchivioLotti from "./pages/ArchivioLotti";
import PrimaNota from "./pages/PrimaNota";
import Frigoriferi from "./pages/Frigoriferi";
// Acquisti pages (nuova sezione)
import Fornitori from "./pages/acquisti/Fornitori";
import AcquistiPage from "./pages/acquisti/AcquistiPage";
import ProdottiPage from "./pages/acquisti/ProdottiPage";
import AnalisiPrezziPage from "./pages/acquisti/AnalisiPrezziPage";
import OrdiniPage from "./pages/acquisti/OrdiniPage";
import FatturaRevisione from "./pages/acquisti/FatturaRevisione";
import StoricoCarichi from "./pages/acquisti/StoricoCarichi";
import ImportFattureSDI from "./pages/acquisti/ImportFattureSDI";
import RevisioneFatturaSDI from "./pages/acquisti/RevisioneFatturaSDI";
import ConfigurazioneFatture from "./pages/acquisti/ConfigurazioneFatture";
// Anagrafica pages
import Profilo from "./pages/anagrafica/Profilo";
// Societa page removed — data managed in abbonamento
import Locali from "./pages/anagrafica/Locali";
import Utenti from "./pages/anagrafica/Utenti";
import Tecnici from "./pages/anagrafica/Tecnici";
import ElementiCucina from "./pages/anagrafica/ElementiCucina";
import Ingredienti from "./pages/anagrafica/Ingredienti";
import Ricette from "./pages/anagrafica/Ricette";
import ProceduraRicetta from "./pages/anagrafica/ProceduraRicetta";
import GestioneCategorie from "./pages/anagrafica/GestioneCategorie";
import IntegrazioneTelegram from "./pages/anagrafica/IntegrazioneTelegram";
import ServiziDelivery from "./pages/anagrafica/ServiziDelivery";
import ProgrammaPulizie from "./pages/ProgrammaPulizie";
import Menu from "./pages/menu/Menu";
import TaskGestione from "./pages/TaskGestione";
import CalendarioAttivita from "./pages/CalendarioAttivita";
import CalendarioAdmin from "./pages/CalendarioAdmin";
import ArchivioDocumenti from "./pages/ArchivioDocumenti";
// Personale pages
import { Turni, FeriePermessi, Presenze, SchedePersonale } from "./pages/personale";
import TurniMobile from "./pages/personale/TurniMobile";
import MieiTurni from "./pages/personale/MieiTurni";
import SuperAdminApp from "./pages/superadmin/SuperAdminApp";
import VenditoreApp from "./pages/venditore/VenditoreApp";
import Register from "./pages/Register";
import VerifyEmail from "./pages/VerifyEmail";
import ReferralRedirect from "./pages/ReferralRedirect";
import LandingPage from "./pages/LandingPage";
import SoftwareHaccp from "./pages/SoftwareHaccp";
import AbbonamentoPage from "./pages/AbbonamentoPage";
import BillingOverview from "./pages/BillingOverview";
import Segnalazioni from "./pages/operativita/Segnalazioni";
import OrdiniOperatore from "./pages/operatore/OrdiniOperatore";
import MieiDocumenti from "./pages/operatore/MieiDocumenti";
import Layout from "./components/Layout";
import CompanyChooser from "./pages/CompanyChooser";
import OperatoreLayout from "./components/OperatoreLayout";
import "@/App.css";

const ProtectedRoute = ({ children, adminOnly = false, requireCapability = null }) => {
  const { token, loading, isAdmin, deveScegliereLocale, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Caricamento...</div>
      </div>
    );
  }
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  
  // Se la route è solo per admin e l'utente non è admin, redirect alla dashboard operatore
  if (adminOnly && !isAdmin) {
    return <Navigate to="/operatore" replace />;
  }

  // Se richiede una capability specifica (assegnata via funzionalita), admin passa sempre
  if (requireCapability && !isAdmin) {
    const userFunc = user?.funzionalita || [];
    if (!userFunc.includes(requireCapability)) {
      return <Navigate to="/operatore" replace />;
    }
  }
  
  // Soppresso durante navigazione wizard preview (sessionStorage flag impostato da goPrev)
  const wizardNavActive = typeof sessionStorage !== "undefined" && !!sessionStorage.getItem("wizard_nav_step");

  return (
    <Layout>
      {deveScegliereLocale && !wizardNavActive && <LocaleChooser />}
      {children}
    </Layout>
  );
};

// Route protetta per operatori (senza Layout, ha il suo design)
const OperatoreRoute = ({ children }) => {
  const { token, loading, isAdmin, deveScegliereLocale } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Caricamento...</div>
      </div>
    );
  }
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  
  if (isAdmin) {
    return <Navigate to="/" replace />;
  }
  
  return (
    <>
      {deveScegliereLocale && <LocaleChooser />}
      {children}
    </>
  );
};

// Route per operatori che usa il Layout appropriato
const OperatorePageRoute = ({ children, requiredFunc, title }) => {
  const { token, loading, user, isAdmin, deveScegliereLocale } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Caricamento...</div>
      </div>
    );
  }
  
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  
  // Admin usa il Layout standard con sidebar
  if (isAdmin) {
    return (
      <Layout>
        {deveScegliereLocale && <LocaleChooser />}
        {children}
      </Layout>
    );
  }
  
  // Verifica che l'operatore abbia la funzionalità richiesta
  if (requiredFunc && !user?.funzionalita?.includes(requiredFunc)) {
    return <Navigate to="/operatore" replace />;
  }
  
  // Operatori usano OperatoreLayout (senza sidebar, con back button)
  return (
    <OperatoreLayout title={title}>
      {deveScegliereLocale && <LocaleChooser />}
      {children}
    </OperatoreLayout>
  );
};

// Smart Home Route - Landing se non loggati, Dashboard se loggati
const HomeRoute = () => {
  const { token, loading, isAdmin, deveScegliereLocale } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="animate-pulse text-muted-foreground">Caricamento...</div></div>;
  if (!token) return <LandingPage />;
  if (!isAdmin) return <Navigate to="/operatore" replace />;
  return <Layout>{deveScegliereLocale && <LocaleChooser />}<Dashboard /></Layout>;
};

// Route a schermo intero senza layout (solo autenticazione)
const FullScreenRoute = ({ children }) => {
  const { token, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="animate-pulse text-muted-foreground">Caricamento...</div></div>;
  if (!token) return <Navigate to="/login" replace />;
  return children;
};

// Traccia automaticamente ogni cambio di pagina su Google Analytics 4
function GA4PageTracker() {
  const location = useLocation();
  useEffect(() => {
    if (typeof window.gtag !== "function") return;
    window.gtag("event", "page_view", {
      page_path: location.pathname + location.search,
      page_title: document.title,
    });
  }, [location]);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <FeatureProvider>
      <ReactivateProvider>
      <ConfirmProvider>
      <BrowserRouter>
        <GA4PageTracker />
        <PWAUpdateNotice />
        <ImpersonationBanner />
        <CookieBanner />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/scegli-societa" element={<CompanyChooser />} />
          <Route path="/register" element={<Register />} />
          <Route path="/register/success" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/software-haccp" element={<SoftwareHaccp />} />
          <Route path="/ref/:codice" element={<ReferralRedirect />} />
          <Route path="/superadmin" element={<SuperAdminApp />} />
          <Route path="/impersonate-callback" element={<ImpersonateCallback />} />
          <Route path="/lotti/p/:id" element={<LottoPubblico />} />
          <Route path="/venditore" element={<VenditoreApp />} />
          
          {/* Dashboard Operatore (mobile-first, senza Layout) */}
          <Route path="/operatore" element={<OperatoreRoute><OperatoreDashboard /></OperatoreRoute>} />
          
          {/* Avvisi Scorte - accessibile a tutti gli operatori */}
          <Route path="/operatore/avvisi-scorte" element={<OperatoreRoute><AvvisiScorte /></OperatoreRoute>} />
          <Route path="/operatore/ordini" element={<OperatoreRoute><OrdiniOperatore /></OperatoreRoute>} />
          <Route path="/operatore/documenti" element={<OperatoreRoute><MieiDocumenti /></OperatoreRoute>} />
          
          {/* Admin Dashboard */}
          <Route path="/" element={<HomeRoute />} />
          <Route path="/dashboard" element={<ProtectedRoute adminOnly><Dashboard /></ProtectedRoute>} />
          
          {/* Pagine accessibili anche agli operatori (con verifica funzionalità) */}
          <Route path="/etichette" element={<OperatorePageRoute title="Lotti"><Lotti /></OperatorePageRoute>} />
          <Route path="/lotti" element={<OperatorePageRoute title="Lotti"><Lotti /></OperatorePageRoute>} />
          <Route path="/lotti/archivio" element={<OperatorePageRoute title="Archivio Lotti"><ArchivioLotti /></OperatorePageRoute>} />
          <Route path="/frigoriferi" element={<OperatorePageRoute requiredFunc="frigoriferi" title="Frigoriferi"><Frigoriferi /></OperatorePageRoute>} />
          
          {/* Acquisti - nuova sezione */}
          <Route path="/acquisti/prodotti" element={<OperatorePageRoute requiredFunc="acquisti" title="Prodotti"><ProdottiPage /></OperatorePageRoute>} />
          <Route path="/acquisti/prodotti/analisi-prezzi" element={<OperatorePageRoute requiredFunc="acquisti" title="Analisi Prezzi"><AnalisiPrezziPage /></OperatorePageRoute>} />
          <Route path="/acquisti/ordini" element={<OperatorePageRoute requiredFunc="acquisti" title="Ordini"><OrdiniPage /></OperatorePageRoute>} />
          <Route path="/acquisti/fornitori" element={<OperatorePageRoute requiredFunc="fornitori" title="Fornitori"><Fornitori /></OperatorePageRoute>} />
          <Route path="/acquisti/fattura/:id/revisione" element={<OperatorePageRoute requiredFunc="acquisti" title="Revisione Fattura"><FatturaRevisione /></OperatorePageRoute>} />
          <Route path="/acquisti/storico" element={<OperatorePageRoute requiredFunc="acquisti" title="Fatture d'acquisto"><StoricoCarichi /></OperatorePageRoute>} />
          <Route path="/acquisti/import-sdi" element={<OperatorePageRoute requiredFunc="acquisti" title="Fatture di Acquisto"><ImportFattureSDI /></OperatorePageRoute>} />
          <Route path="/acquisti/revisione-fattura-sdi" element={<OperatorePageRoute requiredFunc="acquisti" title="Revisione Fattura SDI"><RevisioneFatturaSDI /></OperatorePageRoute>} />
          <Route path="/acquisti/config-fatture" element={<ProtectedRoute adminOnly><ConfigurazioneFatture /></ProtectedRoute>} />
          {/* Backward compat: vecchie route food/materiali → nuova Prodotti */}
          <Route path="/acquisti/food" element={<Navigate to="/acquisti/prodotti" replace />} />
          <Route path="/acquisti/materiali" element={<Navigate to="/acquisti/prodotti" replace />} />
          
          {/* Redirect vecchie route magazzino alle nuove */}
          <Route path="/magazzino/fornitori" element={<Navigate to="/acquisti/fornitori" replace />} />
          <Route path="/magazzino/carico-scarico" element={<Navigate to="/acquisti/prodotti" replace />} />
          <Route path="/magazzino/inventario" element={<Navigate to="/acquisti/prodotti" replace />} />
          <Route path="/magazzino/materiali" element={<Navigate to="/acquisti/prodotti" replace />} />
          <Route path="/magazzino/fattura/:id/revisione" element={<Navigate to="/acquisti/fattura/:id/revisione" replace />} />
          
          {/* Ricette e Ingredienti - accessibili anche agli operatori */}
          <Route path="/anagrafica/categorie" element={<OperatorePageRoute requiredFunc="ricette" title="Gestione Categorie"><GestioneCategorie /></OperatorePageRoute>} />
          <Route path="/anagrafica/ricette" element={<OperatorePageRoute title="Ricette"><Ricette /></OperatorePageRoute>} />
          <Route path="/anagrafica/ricette/:ricettaId/procedura" element={<FullScreenRoute><ProceduraRicetta /></FullScreenRoute>} />
          <Route path="/anagrafica/ricette/categorie" element={<OperatorePageRoute requiredFunc="ricette" title="Categorie Ricette"><GestioneCategorie /></OperatorePageRoute>} />
          <Route path="/anagrafica/ingredienti" element={<OperatorePageRoute requiredFunc="ingredienti" title="Ingredienti"><Ingredienti /></OperatorePageRoute>} />
          
          {/* Menu - Prodotti in Vendita */}
          <Route path="/menu" element={<OperatorePageRoute requiredFunc="gestione_menu" title="Menu di vendita"><Menu /></OperatorePageRoute>} />
          <Route path="/anagrafica/ingredienti/categorie" element={<OperatorePageRoute requiredFunc="ingredienti" title="Categorie Ingredienti"><GestioneCategorie /></OperatorePageRoute>} />
          <Route path="/anagrafica/elementi-cucina" element={<OperatorePageRoute requiredFunc="elementi_cucina" title="Elementi Cucina"><ElementiCucina /></OperatorePageRoute>} />
          
          {/* Pulizie */}
          <Route path="/programma-pulizie" element={<ProtectedRoute adminOnly><ProgrammaPulizie /></ProtectedRoute>} />
          <Route path="/task-gestione" element={<ProtectedRoute adminOnly><TaskGestione /></ProtectedRoute>} />
          <Route path="/calendario" element={<ProtectedRoute adminOnly><CalendarioAdmin /></ProtectedRoute>} />
          <Route path="/operativita/segnalazioni" element={<OperatorePageRoute title="Segnalazioni"><Segnalazioni /></OperatorePageRoute>} />
          <Route path="/calendario-attivita" element={<OperatorePageRoute title="Calendario"><CalendarioAttivita /></OperatorePageRoute>} />
          
          {/* Personale - Modulo Gestione Turni/Ferie/Presenze */}
          {/* Gestione Turni - accessibile ad admin e operatori con funzionalità "turni" */}
          <Route path="/personale/turni" element={<OperatorePageRoute requiredFunc="turni" title="Gestione Turni"><TurniMobile /></OperatorePageRoute>} />
          <Route path="/personale/ferie" element={<ProtectedRoute adminOnly><FeriePermessi /></ProtectedRoute>} />
          <Route path="/personale/presenze" element={<ProtectedRoute adminOnly><Presenze /></ProtectedRoute>} />
          <Route path="/personale/schede" element={<ProtectedRoute adminOnly><SchedePersonale /></ProtectedRoute>} />
          
          {/* Personale - Viste Operatore (sempre disponibili per tutti gli operatori) */}
          <Route path="/operatore/presenze" element={<OperatorePageRoute title="Presenze"><Presenze /></OperatorePageRoute>} />
          <Route path="/operatore/turni" element={<OperatorePageRoute title="I miei turni"><MieiTurni /></OperatorePageRoute>} />
          <Route path="/operatore/ferie" element={<OperatorePageRoute title="Ferie & Permessi"><FeriePermessi /></OperatorePageRoute>} />
          <Route path="/operatore/segnalazioni" element={<OperatorePageRoute title="Segnalazioni"><Segnalazioni /></OperatorePageRoute>} />
          
          {/* Anagrafica Routes - Solo Admin */}
          <Route path="/anagrafica/profilo" element={<OperatorePageRoute title="Il mio Profilo"><Profilo /></OperatorePageRoute>} />
          {/* /anagrafica/societa removed */}
          <Route path="/anagrafica/locali" element={<ProtectedRoute adminOnly><Locali /></ProtectedRoute>} />
          <Route path="/anagrafica/utenti" element={<ProtectedRoute adminOnly><Utenti /></ProtectedRoute>} />
          <Route path="/anagrafica/tecnici" element={<OperatorePageRoute title="Contatti utili"><Tecnici /></OperatorePageRoute>} />
          <Route path="/integrazioni/telegram" element={<ProtectedRoute adminOnly><IntegrazioneTelegram /></ProtectedRoute>} />
          <Route path="/amministrazione/servizi-delivery" element={<ProtectedRoute adminOnly><ServiziDelivery /></ProtectedRoute>} />
          <Route path="/prima-nota" element={<ProtectedRoute adminOnly><PrimaNota /></ProtectedRoute>} />
          <Route path="/archivio-documenti" element={<ProtectedRoute adminOnly><ArchivioDocumenti /></ProtectedRoute>} />
          <Route path="/abbonamento" element={<ProtectedRoute><AbbonamentoPage /></ProtectedRoute>} />
          <Route path="/billing-overview" element={<ProtectedRoute><BillingOverview /></ProtectedRoute>} />
          
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-center" />
      </BrowserRouter>
      </ConfirmProvider>
      </ReactivateProvider>
      </FeatureProvider>
    </AuthProvider>
  );
}

export default App;
